import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Form } from 'antd';
import { useForm } from 'antd/es/form/Form';

import { LoanContext } from '../../LoanDetails';
import { LimitFiles } from '../constants';
import { useCreateLoanDoc, useDeleteFileLoanDoc } from 'services/loan/mutations';
import { useGetLoanDocByLoan } from 'services/loan/querys';
import { useGetTasksByLoan } from 'services/task/querys';
import { LoanDocState, ReplicationStrategy } from 'gql/graphql';
import DropdownBasic from 'components/Dropdowns/DropdownBasic/DropdownBasic';
import CardUploadResponse from 'components/BorrowerTask/components/Atoms/Cards/CardUploadResponse/CardUploadResponse';
import { getStatusOptions } from '../TaskLinkedDocs/statusConfig';
import { useSequentialFileUpload } from '../hooks/useSequentialFileUpload';

interface IClosingDocsFormProps {
  selectLoanDoc: any;
  loanDocs?: any[];
}

const ClosingDocsForm: React.FC<IClosingDocsFormProps> = ({ selectLoanDoc, loanDocs }) => {
  const [form] = useForm();
  const loanSelected: any = useContext(LoanContext);

  const { execute: createLoanDoc } = useCreateLoanDoc();
  const { execute: deleteFileLoanDoc } = useDeleteFileLoanDoc();
  const { reexecute: getLoanDocByLoan } = useGetLoanDocByLoan({ 
    variables: { loanId: loanSelected?._id }, 
    pause: true 
  });
  const { reexecute: getTasksByLoan } = useGetTasksByLoan({
    variables: { loanId: loanSelected?._id },
    pause: true
  });

  const [statusLoanDoc, setStatusLoanDoc] = useState<string>();
  const [fileLoanDoc, setFileLoanDoc] = useState<any[] | undefined>(undefined);
  const [isSelectChanged, setIsSelectChanged] = useState<boolean>(false);

  const isLoanDoc = loanDocs?.find(item => 
    item.key === selectLoanDoc?.key || item._id === selectLoanDoc?._id
  );

  // Hook de cola secuencial
  const uploadQueue = useSequentialFileUpload({
    documentKey: selectLoanDoc?.key,
    loanId: loanSelected._id,
    initialDocumentId: isLoanDoc?._id,
    state: statusLoanDoc || LoanDocState.Pending,
    name: selectLoanDoc?.label,
    isMultipleFiles: true,
    onUploadFile: createLoanDoc,
    onComplete: async () => {
      
      await Promise.all([
        getLoanDocByLoan({ requestPolicy: 'network-only' }),
         getTasksByLoan({ requestPolicy: 'network-only' })
      ]);
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setIsSelectChanged(false);
    },
    additionalPayload: {
      replicationStrategy: ReplicationStrategy.CopyToAll
    }
  });

  useEffect(() => {
    if (selectLoanDoc) {
      form.setFieldValue("name", selectLoanDoc.label);
    }

    if (isLoanDoc?.state) {
      setStatusLoanDoc(isLoanDoc.state);
    }
  }, [selectLoanDoc, isLoanDoc, form]);

  // Cargar archivos existentes de BD
  useEffect(() => {

    if (uploadQueue.isProcessing) {
      return;
    }

    const found = loanDocs?.find(item =>
      item.key === selectLoanDoc?.key || item._id === selectLoanDoc?._id
    );
    let initial: any[] | undefined = found?.file && found?.files?.length === 0
      ? [found.file]
      : found?.files ? found.files : undefined;

    setFileLoanDoc(initial);
  }, [loanDocs, selectLoanDoc, uploadQueue.isProcessing]);

  const onFinish = async (files: any[]) => {
    const newFiles = files?.filter((item: any) =>
      !!item.size && !item.url && !item.key && !item.queueId && !item.isTemporary
    );

    if (newFiles && newFiles.length > 0) {
      const fileStates = uploadQueue.addFiles(newFiles);

      const tempFiles = fileStates.map(fs => ({
        name: fs.file.name,
        size: fs.file.size,
        type: fs.file.type,
        queueId: fs.id,
        isTemporary: true
      }));

      setFileLoanDoc(prev => {
        const keptFiles = prev?.filter(f =>
          f.url || f.key || f.isTemporary
        ) || [];

        const withoutProcessedFiles = keptFiles.filter(f => {
          if (f.isTemporary) return true;
          if (f.url || f.key) return true;
          return !newFiles.some((nf: File) => nf.name === f.name);
        });

        return [...withoutProcessedFiles, ...tempFiles];
      });

      await uploadQueue.startQueue(fileStates);
    } else if (isSelectChanged) {
      const payload: any = {
        key: selectLoanDoc?.key,
        loan: loanSelected._id,
        state: statusLoanDoc,
        name: selectLoanDoc?.label,
        replicationStrategy: ReplicationStrategy.CopyToAll
      };

      if (isLoanDoc?._id) {
        payload._id = isLoanDoc._id;
      }

      await createLoanDoc(payload);

      await Promise.all([
        getLoanDocByLoan({ requestPolicy: 'network-only' }),
        getTasksByLoan({ requestPolicy: 'network-only' })
      ]);
      setIsSelectChanged(false);
    }
  };

  const handlerSelectStatus = async (value: string) => {
    setStatusLoanDoc(value);
    setIsSelectChanged(true);
  };

  const onDeleteFile = async (fileToDelete: any) => {
    if (isLoanDoc && isLoanDoc.file) {
      await deleteFileLoanDoc({ fileKey: isLoanDoc.file.key, loanDocId: isLoanDoc._id });
      setFileLoanDoc([]);
    }
    if (isLoanDoc && isLoanDoc.files && isLoanDoc.files.length > 0 && fileToDelete?.key) {
      await deleteFileLoanDoc({ fileKey: fileToDelete.key, loanDocId: isLoanDoc._id, multipleFiles: true });
      setFileLoanDoc((prev) => prev?.filter((file) => file.key !== fileToDelete.key));
    }
    
    await Promise.all([
      getLoanDocByLoan({ requestPolicy: 'network-only' }),
      getTasksByLoan({ requestPolicy: 'network-only' })
    ]);
  };

  const isDocumentsRequired = () => {
    return statusLoanDoc === LoanDocState.Approved;
  };

  const isCardDisabled = () => {
    if (uploadQueue.isProcessing) return true;
    if (uploadQueue.allSuccess) return true;
    if (!isSelectChanged) return true;

    const newFilesCount = fileLoanDoc?.filter((file: any) => !file.url && !file.key).length || 0;
    
    if (isDocumentsRequired() && newFilesCount === 0 && (!fileLoanDoc || fileLoanDoc.length === 0)) {
      return true;
    }

    return false;
  };

  // Memoizar uploadQueueFiles para evitar recrearlo en cada render
  const uploadQueueFilesWithRetry = useMemo(() => {
    return uploadQueue.files.map(f => ({
      ...f,
      onRetry: () => uploadQueue.retryFile(f.id)
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadQueue.files, uploadQueue.updateCount]);

  return (
    <>
      <DropdownBasic
        className='mt-20'
        label="Select status"
        items={getStatusOptions('closing_docs')}
        selected={statusLoanDoc}
        onClickSelect={handlerSelectStatus}
      />

      <Form
        form={form}
        name="closing-docs-form"
        className='form'
        autoComplete="off"
        onFinish={onFinish}
      >
        <CardUploadResponse
          title=""
          titleLocation='outside'
          limitFiles={LimitFiles.MULTIPLE_FILES}
          actionDeleteFile={onDeleteFile}
          files={fileLoanDoc}
          uploadQueueFiles={uploadQueueFilesWithRetry}
          uploadQueue={uploadQueue}
          onChangeFiles={(files: any[]) => {
            setFileLoanDoc(files);
          }}
          submit={{
            title: 'Publish to borrower',
            disabled: isCardDisabled(),
            loading: uploadQueue.isProcessing,
            modalConfirm: true,
            confirmDescription: "After submitting, you'll have to contact us to make any changes.",
            onClick: (files) => onFinish(files),
            location: 'outside'
          }}
          isCreditReportDocument={false}
          isLoanDoc={isLoanDoc}
          hasDropdown={true}
          messageErrorDisabled='Confirm dropdown status'
          isSelectChanged={isSelectChanged}
        />
      </Form>
    </>
  );
};

export default ClosingDocsForm;
